import { createFileRoute, useParams, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useChat } from "@ai-sdk/react";
import { DefaultChatTransport, type UIMessage } from "ai";
import { useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { Send, Loader2, Sparkles, AlertTriangle, RefreshCcw, ArrowDown, FileText, Square, Copy, ThumbsUp, ThumbsDown, Check } from "lucide-react";
import { getAiThread, saveAiTurn } from "@/lib/ai-buddha.functions";
import { listAiModes, submitAiFeedback, getMyFeedback, type AiModeSummary } from "@/lib/ai-modes.functions";
import { track } from "@/lib/analytics";

type Level = "beginner" | "intermediate" | "advanced";


export const Route = createFileRoute("/_authenticated/ask/$threadId")({
  component: ThreadPage,
});

const QUICK_PROMPTS = [
  "Where do I begin?",
  "A practice for grief",
  "What should I read first?",
  "Explain KAfrequency in one paragraph",
];

function extractText(m: UIMessage): string {
  return m.parts.map((p) => (p.type === "text" ? p.text : "")).join("");
}

function formatTime(ts: number): { label: string; iso: string } {
  const d = new Date(ts);
  const iso = d.toISOString();
  const label = d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  return { label, iso };
}

/** Render "[1]", "[2]" citation markers as accessible superscripts. */
function CitationText({ text }: { text: string }) {
  const nodes: React.ReactNode[] = [];
  const re = /\[(\d+)\]/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    const n = m[1];
    nodes.push(
      <sup key={`c-${i++}`} className="mx-0.5 text-[0.7em] font-medium text-gold">
        <span aria-label={`Citation ${n}`}>[{n}]</span>
      </sup>,
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return <>{nodes}</>;
}


function ThreadPage() {
  const { threadId } = useParams({ from: "/_authenticated/ask/$threadId" });
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [input, setInput] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLLIElement>(null);
  const messagesListRef = useRef<HTMLOListElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [atBottom, setAtBottom] = useState(true);
  const [transcriptMode, setTranscriptMode] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem("kaf.buddha.transcriptMode") === "1";
  });
  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem("kaf.buddha.transcriptMode", transcriptMode ? "1" : "0");
    }
  }, [transcriptMode]);
  const transcriptRef = useRef<HTMLElement>(null);
  const seenAtRef = useRef<Map<string, number>>(new Map());

  const [level, setLevel] = useState<Level>(() => {
    if (typeof window === "undefined") return "intermediate";
    return ((localStorage.getItem("kaf.buddha.level") as Level) || "intermediate");
  });
  const [mode, setMode] = useState<string>(() => {
    if (typeof window === "undefined") return "ask";
    return localStorage.getItem("kaf.buddha.mode") || "ask";
  });
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  useEffect(() => {
    if (typeof window !== "undefined") localStorage.setItem("kaf.buddha.level", level);
  }, [level]);
  useEffect(() => {
    if (typeof window !== "undefined") localStorage.setItem("kaf.buddha.mode", mode);
  }, [mode]);


  const getThreadFn = useServerFn(getAiThread);
  const saveTurnFn = useServerFn(saveAiTurn);
  const listModesFn = useServerFn(listAiModes);
  const submitFeedbackFn = useServerFn(submitAiFeedback);
  const getMyFeedbackFn = useServerFn(getMyFeedback);

  const modesQuery = useQuery({
    queryKey: ["ai-modes"],
    queryFn: () => listModesFn(),
    staleTime: 5 * 60_000,
  });
  const modes: AiModeSummary[] = modesQuery.data ?? [];

  const feedbackQuery = useQuery({
    queryKey: ["ai-feedback", threadId],
    queryFn: () => getMyFeedbackFn({ data: { threadId } }),
    staleTime: 30_000,
  });
  const [localFeedback, setLocalFeedback] = useState<Record<string, 1 | -1>>({});
  useEffect(() => {
    if (feedbackQuery.data) {
      const m: Record<string, 1 | -1> = {};
      for (const r of feedbackQuery.data) m[r.message_id] = (r.rating as 1 | -1);
      setLocalFeedback(m);
    }
  }, [feedbackQuery.data]);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const thread = useQuery({
    queryKey: ["ai-thread", threadId],
    queryFn: () => getThreadFn({ data: { threadId } }),
    staleTime: 0,
  });

  const initialMessages = useMemo<UIMessage[]>(() => {
    const j = (thread.data as { messagesJson?: string } | undefined)?.messagesJson;
    if (!j) return [];
    try { return JSON.parse(j) as UIMessage[]; } catch { return []; }
  }, [thread.data]);

  const transport = useMemo(
    () =>
      new DefaultChatTransport({
        api: "/api/ask-buddha",
        fetch: async (input, init) => {
          const { supabase } = await import("@/integrations/supabase/client");
          const { data } = await supabase.auth.getSession();
          const token = data.session?.access_token;
          const headers = new Headers(init?.headers);
          if (token) headers.set("Authorization", `Bearer ${token}`);
          let body: Record<string, unknown> = {};
          if (typeof init?.body === "string") {
            try { body = JSON.parse(init.body); } catch { body = {}; }
          }
          body.level = level;
          body.mode = mode;
          body.threadId = threadId;
          return fetch(input, { ...init, headers, body: JSON.stringify(body) });
        },
      }),
    [level, mode, threadId]
  );

  const pendingUserRef = useRef<UIMessage | null>(null);

  const { messages, sendMessage, status, setMessages, stop, regenerate } = useChat({
    id: threadId,
    messages: initialMessages,
    transport,
    onError: (err: Error) => {
      console.error("[ask-buddha]", err);
      const msg = err?.message || "";
      track("ai_response_error", { mode, code: msg.slice(0, 60) });
      if (msg.includes("429") || /rate/i.test(msg)) {
        setErrorMsg("You've reached the hourly limit. Please try again in a bit.");
      } else if (msg.includes("402") || /credit/i.test(msg)) {
        setErrorMsg("The temple's AI credits are exhausted. Please try again later.");
      } else if (msg.includes("401")) {
        setErrorMsg("Your session expired. Please sign in again.");
      } else {
        setErrorMsg("Something interrupted the transmission. Please retry.");
      }
    },
    onFinish: async ({ message }) => {
      track("ai_response_completed", { mode, level });
      const userMsg = pendingUserRef.current;
      pendingUserRef.current = null;
      if (!userMsg) return;
      try {
        await saveTurnFn({
          data: {
            threadId,
            userMessageJson: JSON.stringify(userMsg),
            assistantMessageJson: JSON.stringify(message),
          },
        });
        qc.invalidateQueries({ queryKey: ["ai-threads"] });
      } catch (e) {
        console.error("[ask-buddha] persist failed", e);
      }
    },
  });

  // Hydrate messages from DB once per threadId (avoid clobbering stream state).
  const hydratedFor = useRef<string | null>(null);
  useEffect(() => {
    if (thread.isError) {
      navigate({ to: "/ask" });
      return;
    }
    if (thread.data && hydratedFor.current !== threadId) {
      setMessages(initialMessages);
      hydratedFor.current = threadId;
    }
  }, [threadId, thread.data, thread.isError, setMessages, navigate, initialMessages]);


  // Auto-scroll on new content only if user is already at bottom
  useEffect(() => {
    if (atBottom && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, status, atBottom]);

  // Track whether the bottom sentinel is visible (user "at latest")
  useEffect(() => {
    const root = scrollRef.current;
    const target = bottomRef.current;
    if (!root || !target) return;
    const io = new IntersectionObserver(
      (entries) => setAtBottom(entries[0]?.isIntersecting ?? true),
      { root, threshold: 0.01 },
    );
    io.observe(target);
    return () => io.disconnect();
  }, [messages.length]);

  // Stamp first-seen time for each message id
  useEffect(() => {
    const map = seenAtRef.current;
    const now = Date.now();
    for (const m of messages) if (!map.has(m.id)) map.set(m.id, now);
  }, [messages]);

  const jumpToLatest = () => {
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    // Move focus to the newest message for keyboard/SR users
    requestAnimationFrame(() => {
      const list = messagesListRef.current;
      const last = list?.querySelector<HTMLElement>("[data-msg]:last-of-type");
      last?.focus();
    });
  };

  // Keyboard nav across message boundaries (Alt+↑ / Alt+↓)
  const onListKeyDown = (e: React.KeyboardEvent<HTMLOListElement>) => {
    if (!e.altKey) return;
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    const list = messagesListRef.current;
    if (!list) return;
    const nodes = Array.from(list.querySelectorAll<HTMLElement>("[data-msg]"));
    if (nodes.length === 0) return;
    const current = document.activeElement as HTMLElement | null;
    const idx = current ? nodes.indexOf(current) : -1;
    const next =
      e.key === "ArrowDown"
        ? Math.min(nodes.length - 1, idx + 1)
        : Math.max(0, idx - 1);
    if (next !== idx && nodes[next]) {
      e.preventDefault();
      nodes[next].focus();
    }
  };



  // Focus composer
  useEffect(() => {
    inputRef.current?.focus();
  }, [threadId]);

  const busy = status === "submitted" || status === "streaming";

  async function send(text: string) {
    const trimmed = text.trim();
    if (!trimmed || busy) return;
    setErrorMsg(null);
    setInput("");
    const userMsg: UIMessage = {
      id: `u_${Date.now().toString(36)}`,
      role: "user",
      parts: [{ type: "text", text: trimmed }],
    };
    pendingUserRef.current = userMsg;
    track("ai_message_sent", { mode, level, length: trimmed.length });
    try {
      await sendMessage({ text: trimmed });
    } catch (e) {
      console.error("[ask-buddha] send failed", e);
    }
  }

  function handleStop() {
    stop?.();
    track("ai_response_stopped", { mode });
  }
  function handleRegenerate() {
    setErrorMsg(null);
    track("ai_response_regenerated", { mode });
    regenerate?.();
  }
  async function handleCopy(id: string, text: string) {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      track("ai_message_copied", { id });
      setTimeout(() => setCopiedId((c: string | null) => (c === id ? null : c)), 1600);
    } catch { /* ignore */ }
  }
  async function handleFeedback(messageId: string, rating: 1 | -1) {
    setLocalFeedback((m) => ({ ...m, [messageId]: rating }));
    track("ai_feedback_submitted", { rating, mode });
    try {
      await submitFeedbackFn({ data: { threadId, messageId, rating, modeKey: mode } });
    } catch (e) {
      console.error("[ask-buddha] feedback failed", e);
    }
  }

  function retryLast() {
    // Find last user message and resend
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m.role === "user") {
        const t = m.parts.map((p) => (p.type === "text" ? p.text : "")).join("");
        if (t) { setErrorMsg(null); void send(t); return; }
      }
    }
  }


  const errorRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (errorMsg) errorRef.current?.focus();
  }, [errorMsg]);

  const threadTitle =
    (thread.data as { title?: string } | undefined)?.title ?? "New conversation";

  return (
    <div className="flex h-full min-h-[70vh] flex-col" aria-busy={busy || undefined}>
      {/* Header */}
      <header
        className="flex items-center gap-3 border-b hairline px-4 py-3 md:px-6"
        aria-label={`Conversation: ${threadTitle}`}
      >
        <Sparkles size={16} className="text-gold" aria-hidden="true" focusable="false" />
        <h1 className="flex-1 font-serif text-lg text-ivory line-clamp-1" title={threadTitle}>
          {threadTitle}
        </h1>
        <button
          type="button"
          onClick={() => {
            setTranscriptMode((v) => {
              const next = !v;
              if (next) {
                requestAnimationFrame(() => transcriptRef.current?.focus());
              }
              return next;
            });
          }}
          aria-pressed={transcriptMode}
          aria-controls="sr-transcript"
          aria-label={
            transcriptMode
              ? "Hide screen-reader transcript of the conversation"
              : "Show screen-reader transcript of the conversation"
          }
          className="inline-flex min-h-9 items-center gap-1 rounded-full border hairline bg-charcoal/40 px-3 py-1 text-xs text-ivory hover:border-gold focus:border-gold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-obsidian"
        >
          <FileText size={12} aria-hidden="true" focusable="false" />
          <span>Transcript</span>
        </button>
        <label htmlFor="buddha-level" className="sr-only">
          Teaching level
        </label>
        <select
          id="buddha-level"
          value={level}
          onChange={(e) => setLevel(e.target.value as Level)}
          disabled={busy}
          aria-label="Teaching level for AI Buddha KA"
          aria-describedby="buddha-level-help"
          className="min-h-9 rounded-full border hairline bg-charcoal/40 px-3 py-1 text-xs text-ivory focus:border-gold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-obsidian disabled:opacity-60"
        >
          <option value="beginner">Beginner</option>
          <option value="intermediate">Intermediate</option>
          <option value="advanced">Advanced</option>
        </select>
        <span id="buddha-level-help" className="sr-only">
          Adjusts how AI Buddha KA explains teachings. Changes apply to your next message.
        </span>
        <label htmlFor="buddha-mode" className="sr-only">Mode</label>
        <select
          id="buddha-mode"
          value={mode}
          onChange={(e) => { setMode(e.target.value); track("ai_mode_selected", { mode: e.target.value }); }}
          disabled={busy || modes.length === 0}
          aria-label="AI Buddha KA mode"
          className="min-h-9 rounded-full border hairline bg-charcoal/40 px-3 py-1 text-xs text-ivory focus:border-gold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-obsidian disabled:opacity-60"
        >
          {modes.length === 0 && <option value={mode}>Ask</option>}
          {modes.map((m) => (
            <option key={m.key} value={m.key} title={m.description}>{m.label}</option>
          ))}
        </select>
      </header>

      {/* Screen-reader transcript: linear, in-order retelling of the conversation
          with expanded citations and full timestamps. Toggled by the Transcript button. */}
      <section
        id="sr-transcript"
        ref={transcriptRef}
        tabIndex={-1}
        aria-label="Screen-reader transcript of the conversation with AI Buddha KA"
        aria-live="polite"
        aria-atomic="false"
        className={
          transcriptMode
            ? "mx-4 mt-3 max-h-[40vh] overflow-y-auto rounded-xl border hairline bg-charcoal/30 p-4 text-sm text-ivory focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold md:mx-6"
            : "sr-only"
        }
      >
        {messages.length === 0 ? (
          <p>No messages yet in this conversation.</p>
        ) : (
          <ol className="space-y-3">
            {messages.map((m, i) => {
              const raw = extractText(m);
              // Expand "[1]" markers so screen readers say "citation 1" in flow.
              const spoken = raw.replace(/\[(\d+)\]/g, " (citation $1) ");
              const ts = seenAtRef.current.get(m.id) ?? Date.now();
              const d = new Date(ts);
              const iso = d.toISOString();
              const spokenTime = d.toLocaleString(undefined, {
                weekday: "long",
                hour: "numeric",
                minute: "2-digit",
              });
              const roleName = m.role === "user" ? "You" : "AI Buddha KA";
              return (
                <li key={`t-${m.id}`}>
                  <p>
                    <span className="font-medium">
                      Turn {i + 1} of {messages.length}. {roleName} said on{" "}
                      <time dateTime={iso}>{spokenTime}</time>:
                    </span>{" "}
                    <span className="whitespace-pre-wrap">{spoken || "(no text)"}</span>
                  </p>
                </li>
              );
            })}
            {status === "submitted" || status === "streaming" ? (
              <li>
                <p>AI Buddha KA is still responding.</p>
              </li>
            ) : (
              <li>
                <p>End of transcript.</p>
              </li>
            )}
          </ol>
        )}
      </section>

      {/* Polite status region for stream state, always mounted for SR updates */}
      <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {status === "submitted"
          ? "AI Buddha KA is preparing a response."
          : status === "streaming"
            ? "AI Buddha KA is responding."
            : ""}
      </div>

      {errorMsg && (
        <div
          ref={errorRef}
          role="alert"
          aria-live="assertive"
          tabIndex={-1}
          className="mx-4 mt-3 flex items-start gap-3 rounded-xl border border-rose-500/40 bg-rose-500/10 px-4 py-3 text-sm text-rose-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-300 md:mx-6"
        >
          <AlertTriangle
            size={16}
            className="mt-0.5 shrink-0 text-rose-300"
            aria-hidden="true"
            focusable="false"
          />
          <div className="flex-1">
            <p>
              <span className="sr-only">Error: </span>
              {errorMsg}
            </p>
          </div>
          <button
            type="button"
            onClick={retryLast}
            aria-label="Retry sending the last message"
            className="inline-flex min-h-9 items-center gap-1 rounded-full border border-rose-300/50 px-3 py-1 text-xs text-rose-100 hover:bg-rose-500/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-300 focus-visible:ring-offset-2 focus-visible:ring-offset-obsidian"
          >
            <RefreshCcw size={12} aria-hidden="true" focusable="false" />
            <span>Retry</span>
          </button>
          <button
            type="button"
            onClick={() => {
              setErrorMsg(null);
              inputRef.current?.focus();
            }}
            aria-label="Dismiss error"
            className="inline-flex min-h-9 min-w-9 items-center justify-center rounded-full text-rose-100/80 hover:text-rose-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-300"
          >
            <span aria-hidden="true">×</span>
          </button>
        </div>
      )}



      {/* Messages */}
      <div
        ref={scrollRef}
        className="relative flex-1 overflow-y-auto px-4 py-6 md:px-8"
      >
        {messages.length === 0 && !busy && (
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-sm text-muted-foreground">Ask AI Buddha KA anything.</p>
            <div className="mt-6 flex flex-wrap justify-center gap-2">
              {QUICK_PROMPTS.map((p) => (
                <button
                  key={p}
                  onClick={() => send(p)}
                  className="rounded-full border hairline px-4 py-2 text-xs text-muted-foreground hover:border-gold hover:text-gold min-h-[36px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-obsidian"
                >
                  {p}
                </button>
              ))}
            </div>
          </div>
        )}

        <ol
          ref={messagesListRef}
          role="log"
          aria-label={`Conversation with AI Buddha KA — ${messages.length} message${messages.length === 1 ? "" : "s"}`}
          aria-live="polite"
          aria-relevant="additions"
          onKeyDown={onListKeyDown}
          className="mx-auto flex max-w-2xl list-none flex-col gap-6 p-0"
        >
          {messages.map((m, i) => {
            const text = extractText(m);
            const ts = seenAtRef.current.get(m.id) ?? Date.now();
            const { label, iso } = formatTime(ts);
            const roleName = m.role === "user" ? "You" : "AI Buddha KA";
            const isUser = m.role === "user";
            return (
              <li
                key={m.id}
                data-msg
                data-role={m.role}
                tabIndex={-1}
                aria-posinset={i + 1}
                aria-setsize={messages.length}
                aria-label={`${roleName} at ${label}`}
                className={`group scroll-mt-4 rounded-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-obsidian ${isUser ? "self-end" : "self-start w-full"}`}
              >
                <div
                  className={`mb-1 flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground ${isUser ? "justify-end" : ""}`}
                >
                  <span aria-hidden="true">{roleName}</span>
                  <span aria-hidden="true">·</span>
                  <time dateTime={iso}>
                    <span className="sr-only">{roleName} said at </span>
                    {label}
                  </time>
                </div>
                {isUser ? (
                  <div className="flex justify-end">
                    <div className="max-w-[85%] rounded-2xl rounded-br-sm bg-primary px-4 py-3 text-primary-foreground">
                      <p className="whitespace-pre-wrap text-sm leading-relaxed">
                        <CitationText text={text} />
                      </p>
                    </div>
                  </div>
                ) : (
                  <article
                    aria-label={`Response from AI Buddha KA at ${label}`}
                    className="prose prose-invert prose-sm max-w-none text-ivory prose-headings:font-serif prose-a:text-gold prose-a:no-underline hover:prose-a:text-gold-luminous prose-blockquote:border-l-gold prose-blockquote:text-gold-luminous prose-blockquote:italic"
                  >
                    <ReactMarkdown
                      components={{
                        p: ({ children }) => (
                          <p>
                            {typeof children === "string" ? (
                              <CitationText text={children} />
                            ) : (
                              children
                            )}
                          </p>
                        ),
                      }}
                    >
                      {text || " "}
                    </ReactMarkdown>
                  </article>
                )}
                {!isUser && text && status !== "streaming" && (
                  <div className="mt-2 flex items-center gap-1 text-muted-foreground opacity-60 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                    <button
                      type="button"
                      onClick={() => handleCopy(m.id, text)}
                      aria-label={copiedId === m.id ? "Copied" : "Copy response"}
                      className="inline-flex h-8 min-w-8 items-center gap-1 rounded-full border hairline px-2 text-[11px] hover:text-ivory hover:border-gold/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold"
                    >
                      {copiedId === m.id ? <Check size={12} aria-hidden="true" /> : <Copy size={12} aria-hidden="true" />}
                      <span>{copiedId === m.id ? "Copied" : "Copy"}</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleFeedback(m.id, 1)}
                      aria-pressed={localFeedback[m.id] === 1}
                      aria-label="Mark response helpful"
                      className={`inline-flex h-8 min-w-8 items-center justify-center rounded-full border hairline px-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold ${localFeedback[m.id] === 1 ? "border-gold text-gold" : "hover:text-ivory hover:border-gold/50"}`}
                    >
                      <ThumbsUp size={12} aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleFeedback(m.id, -1)}
                      aria-pressed={localFeedback[m.id] === -1}
                      aria-label="Mark response unhelpful"
                      className={`inline-flex h-8 min-w-8 items-center justify-center rounded-full border hairline px-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold ${localFeedback[m.id] === -1 ? "border-gold text-gold" : "hover:text-ivory hover:border-gold/50"}`}
                    >
                      <ThumbsDown size={12} aria-hidden="true" />
                    </button>
                    {i === messages.length - 1 && (
                      <button
                        type="button"
                        onClick={handleRegenerate}
                        disabled={busy}
                        aria-label="Regenerate response"
                        className="inline-flex h-8 min-w-8 items-center gap-1 rounded-full border hairline px-2 text-[11px] hover:text-ivory hover:border-gold/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold disabled:opacity-40"
                      >
                        <RefreshCcw size={12} aria-hidden="true" />
                        <span>Regenerate</span>
                      </button>
                    )}
                  </div>
                )}
              </li>
            );
          })}
          {status === "submitted" && (
            <li className="flex items-center gap-2 text-sm text-muted-foreground" aria-hidden="true">
              <Loader2 size={14} className="animate-spin text-gold" />
              <span className="italic">The current is gathering…</span>
            </li>
          )}
          <li ref={bottomRef} aria-hidden="true" className="h-px w-full list-none" />
        </ol>

        {!atBottom && messages.length > 0 && (
          <button
            type="button"
            onClick={jumpToLatest}
            aria-label="Jump to latest message"
            className="pointer-events-auto sticky bottom-3 z-10 mx-auto flex min-h-9 items-center gap-1 rounded-full border hairline bg-obsidian/90 px-3 py-1.5 text-xs text-ivory shadow-lg backdrop-blur focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-obsidian"
          >
            <ArrowDown size={12} aria-hidden="true" focusable="false" />
            <span>Jump to latest</span>
          </button>
        )}
      </div>


      {/* Composer */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          send(input);
        }}
        className="border-t hairline bg-obsidian/40 px-4 py-3 md:px-6"
      >
        <div className="mx-auto flex max-w-2xl items-end gap-2 rounded-2xl border hairline bg-charcoal/40 px-3 py-2 focus-within:border-gold">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send(input);
              }
            }}
            rows={1}
            placeholder="Ask AI Buddha KA…"
            disabled={busy}
            aria-label="Ask AI Buddha KA"
            className="flex-1 resize-none bg-transparent px-2 py-2 text-sm text-ivory placeholder:text-muted-foreground focus:outline-none disabled:opacity-60"
            style={{ maxHeight: 160 }}
          />
          {busy ? (
            <button
              type="button"
              onClick={handleStop}
              aria-label="Stop generating"
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border hairline bg-charcoal/40 text-ivory hover:border-gold/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold"
            >
              <Square size={14} aria-hidden="true" />
            </button>
          ) : (
            <button
              type="submit"
              disabled={!input.trim()}
              aria-label="Send"
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gold-gradient text-obsidian shadow-gold disabled:opacity-50"
            >
              <Send size={14} />
            </button>
          )}
        </div>
        <p className="mx-auto mt-2 max-w-2xl text-center text-[10px] uppercase tracking-widest text-muted-foreground">
          Trained only on the KAfrequency canon
        </p>
      </form>
    </div>
  );
}
